import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { QRCodeCanvas as QRCode } from 'qrcode.react';
import { toast } from 'react-toastify';
import { 
  getUserObjects, 
  getObject, 
  claimObject, 
  updateObject, 
  deleteObject 
} from '../services/api';
import './Dashboard.css';

const Dashboard = () => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  
  const [userObjects, setUserObjects] = useState([]);
  const [selectedObject, setSelectedObject] = useState(null);
  const [expandedQR, setExpandedQR] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeDropdown, setActiveDropdown] = useState(null);
  
  // Add seed for connecting existing objects to account
  const [seedInput, setSeedInput] = useState('');
  const [showSeedForm, setShowSeedForm] = useState(false);
  const [claimLoading, setClaimLoading] = useState(false);
  const [showActionPanel, setShowActionPanel] = useState(false);
  
  useEffect(() => {
    // Fetch user objects when component mounts
    fetchUserObjects();
    
    // Adaugă handler pentru a închide meniurile dropdown când se face click în afara lor
    const handleClickOutside = (event) => {
      if (!event.target.closest('.dropdown-menu') && !event.target.closest('.action-dots')) {
        setActiveDropdown(null);
      }
    };
    
    document.addEventListener('click', handleClickOutside);
    
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);
  
  const fetchUserObjects = async () => {
    try {
      setLoading(true);
      const objects = await getUserObjects();
      setUserObjects(objects);
      
      // Select first object by default if exists
      if (objects.length > 0 && !selectedObject) {
        const fullObject = await getObject(objects[0].id);
        setSelectedObject(fullObject);
      }
    } catch (error) {
      console.error('Error fetching objects:', error);
      toast.error('Nu am putut încărca obiectele tale.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      toast.error('Nu am putut deconecta contul.');
    }
  };
  
  const handleSelectObject = async (objectId) => {
    try {
      setLoading(true);
      const objectData = await getObject(objectId);
      setSelectedObject(objectData);
      setShowActionPanel(false);
    } catch (error) {
      console.error('Error selecting object:', error);
      toast.error('Nu am putut încărca detaliile obiectului.');
    } finally {
      setLoading(false);
    }
  };
  
  const updateObjectStatus = async (objectId, newStatus) => {
    try {
      const updatedObject = await updateObject(objectId, { status: newStatus });
      
      // Update local state
      setSelectedObject(updatedObject);
      setUserObjects(prevObjects => 
        prevObjects.map(obj => 
          obj.id === objectId ? { ...obj, status: newStatus } : obj
        )
      );
      
      toast.success(`Statusul obiectului a fost actualizat la: ${newStatus}`);
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Nu am putut actualiza statusul obiectului.');
    }
  };
  
  const updateVisibility = async (objectId, isVisible) => {
    try {
      const updatedObject = await updateObject(objectId, { is_public: isVisible });
      
      // Update local state
      setSelectedObject(updatedObject);
      
      toast.success('Vizibilitatea contactelor a fost actualizată');
    } catch (error) {
      console.error('Error updating visibility:', error);
      toast.error('Nu am putut actualiza vizibilitatea contactelor.');
    }
  };
  
  const handleDeleteObject = async (objectId) => {
    if (window.confirm('Ești sigur că vrei să ștergi acest obiect? Această acțiune nu poate fi anulată.')) {
      try {
        await deleteObject(objectId);
        
        // Update local state
        setUserObjects(prevObjects => prevObjects.filter(obj => obj.id !== objectId));
        
        if (selectedObject && selectedObject.id === objectId) {
          setSelectedObject(null);
        }
        
        toast.success('Obiect șters cu succes');
      } catch (error) {
        console.error('Error deleting object:', error);
        toast.error('Nu am putut șterge obiectul.');
      }
    }
  };
  
  const downloadQRCode = (objectId) => {
    const canvas = document.getElementById(`qr-code-${objectId}`);
    if (canvas) {
      const pngUrl = canvas
        .toDataURL('image/png')
        .replace('image/png', 'image/octet-stream');
      
      const downloadLink = document.createElement('a');
      downloadLink.href = pngUrl;
      downloadLink.download = `antifurt-qr-${objectId}.png`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
    }
  };
  
  const toggleActionDropdown = (objectId) => {
    setActiveDropdown(activeDropdown === objectId ? null : objectId);
  };
  
  const handleClaimObject = async () => {
    if (!seedInput.trim()) {
      return toast.error('Te rugăm să introduci seed-ul.');
    }
    
    try {
      setClaimLoading(true);
      const claimedObject = await claimObject(seedInput.trim());
      
      // Update local state with the new object
      setUserObjects(prevObjects => [...prevObjects, claimedObject]);
      setSelectedObject(claimedObject);
      
      toast.success('Obiect adăugat cu succes în contul tău');
      setSeedInput('');
      setShowSeedForm(false);
    } catch (error) {
      console.error('Error claiming object:', error);
      toast.error('Nu am putut adăuga obiectul. Verifică seed-ul și încearcă din nou.');
    } finally {
      setClaimLoading(false);
    }
  };
  
  const viewPublic = (objectId) => {
    window.open(`/status/${objectId}`, '_blank');
  };
  
  if (loading && userObjects.length === 0) {
    return (
      <div className="dashboard-container">
        <div className="loading">
          <div className="spinner"></div>
          <p>Se încarcă dashboard-ul...</p>
        </div>
      </div>
    );
  }
  
  // Funcții de randare a obiectelor
  const renderStatusBadge = (status) => {
    const labels = {
      'active': 'Activ',
      'lost': 'Pierdut',
      'stolen': 'Furat',
      'default': 'Necunoscut'
    };
    
    return (
      <span className={`status-badge ${status}`}>
        {labels[status] || labels.default}
      </span>
    );
  };
  
  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1>Obiectele Mele</h1>
        <div className="user-info">
          <p>Conectat ca: <strong>{currentUser.email}</strong></p>
          <button onClick={handleLogout} className="logout-btn">Deconectare</button>
        </div>
      </div>
      
      {/* Vizualizare card pentru obiecte */}
      {userObjects.length > 0 ? (
        <div className="cards-grid">
          {userObjects.map((obj) => (
            <div
              key={obj.id}
              className={`object-card ${selectedObject && selectedObject.id === obj.id ? 'selected' : ''} ${obj.status}`}
              onClick={() => handleSelectObject(obj.id)}
            >
              <div className="card-header">
                <h3 className="card-title">{obj.description}</h3>
                <div className="card-actions">
                  <button 
                    className="action-dots" 
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleActionDropdown(obj.id);
                    }}
                  >
                    <span></span>
                    <span></span>
                    <span></span>
                  </button>
                  
                  {activeDropdown === obj.id && (
                    <div className="dropdown-menu">
                      <button onClick={(e) => {
                        e.stopPropagation();
                        viewPublic(obj.id);
                      }}>
                        Vizualizare publică
                      </button>
                      <button onClick={(e) => {
                        e.stopPropagation();
                        handleSelectObject(obj.id);
                      }}>
                        Editare
                      </button>
                      <button onClick={(e) => {
                        e.stopPropagation();
                        updateObjectStatus(obj.id, 'active');
                      }}>
                        Marcă ca Activ
                      </button>
                      <button onClick={(e) => {
                        e.stopPropagation();
                        updateObjectStatus(obj.id, 'lost');
                      }}>
                        Marcă ca Pierdut
                      </button>
                      <button onClick={(e) => {
                        e.stopPropagation();
                        updateObjectStatus(obj.id, 'stolen');
                      }}>
                        Marcă ca Furat
                      </button>
                      <button className="delete-action" onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteObject(obj.id);
                      }}>
                        Șterge
                      </button>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="card-body">
                <div className="card-info">
                  <p><strong>Contact:</strong> {obj.full_name}</p>
                  <p><strong>Telefon:</strong> {obj.phone_number}</p>
                </div>
                <div className="card-status">
                  <div className="status-info">
                    {renderStatusBadge(obj.status)}
                  </div>
                  <div className="mini-qr">
                    <QRCode
                      value={`${window.location.origin}/status/${obj.id}`}
                      size={50}
                      level="M"
                      renderAs="svg"
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}
          
          {/* Card pentru adăugare obiect nou */}
          <div className="object-card add-card" onClick={() => navigate('/')}>
            <div className="add-icon">+</div>
            <p>Adaugă obiect nou</p>
          </div>
        </div>
      ) : (
        <div className="empty-state">
          <div className="empty-icon">🔍</div>
          <h2>Nu ai niciun obiect încă</h2>
          <p>Adaugă primul tău obiect pentru a-l proteja cu un cod QR.</p>
          <div className="empty-actions">
            <button 
              className="btn primary-btn"
              onClick={() => navigate('/')}
            >
              Creează Primul Obiect
            </button>
            <button 
              className="btn secondary-btn"
              onClick={() => setShowSeedForm(true)}
            >
              Adaugă Obiect Existent cu Seed
            </button>
          </div>
        </div>
      )}
      
      {/* Panou de detalii pentru obiectul selectat */}
      {selectedObject && (
        <div className="detail-panel">
          <div className="detail-header">
            <h2>{selectedObject.description}</h2>
            <button className="close-details" onClick={() => setSelectedObject(null)}>×</button>
          </div>
          
          <div className="detail-tabs">
            <button 
              className={`tab-btn ${!showActionPanel ? 'active' : ''}`}
              onClick={() => setShowActionPanel(false)}
            >
              Informații
            </button>
            <button 
              className={`tab-btn ${showActionPanel ? 'active' : ''}`}
              onClick={() => setShowActionPanel(true)}
            >
              Acțiuni
            </button>
          </div>
          
          {!showActionPanel ? (
            <div className="detail-info">
              <div className="info-section">
                <h3>Informații de contact</h3>
                <div className="info-grid">
                  <div className="info-row">
                    <span className="info-label">Nume:</span>
                    <span className="info-value">{selectedObject.full_name}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Telefon:</span>
                    <span className="info-value">{selectedObject.phone_number}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Status:</span>
                    <span className="info-value">{renderStatusBadge(selectedObject.status)}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Vizibilitate:</span>
                    <span className="info-value">{selectedObject.is_public ? 'Public' : 'Privat'}</span>
                  </div>
                </div>
              </div>
              
              <div className="qr-section">
                <h3>Cod QR</h3>
                <div className="qr-container">
                  <QRCode
                    id={`qr-code-${selectedObject.id}`}
                    value={`${window.location.origin}/status/${selectedObject.id}`}
                    size={180}
                    level="H"
                    includeMargin={true}
                  />
                  <div className="qr-actions">
                    <button 
                      className="btn primary-btn"
                      onClick={() => downloadQRCode(selectedObject.id)}
                    >
                      Descarcă
                    </button>
                    <a 
                      href={`/status/${selectedObject.id}`}
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="btn link-btn"
                    >
                      Vizualizare
                    </a>
                  </div>
                </div>
              </div>
              
              <div className="seed-info">
                <h3>Seed de recuperare</h3>
                <div className="seed-display">
                  {selectedObject.seed}
                </div>
              </div>
            </div>
          ) : (
            <div className="action-panel">
              <div className="action-section">
                <h3>Status obiect</h3>
                <div className="action-buttons">
                  <button 
                    className={`action-btn ${selectedObject.status === 'active' ? 'selected' : ''}`}
                    onClick={() => updateObjectStatus(selectedObject.id, 'active')}
                  >
                    <span className="status-icon active"></span>
                    Activ
                  </button>
                  <button 
                    className={`action-btn ${selectedObject.status === 'lost' ? 'selected' : ''}`}
                    onClick={() => updateObjectStatus(selectedObject.id, 'lost')}
                  >
                    <span className="status-icon lost"></span>
                    Pierdut
                  </button>
                  <button 
                    className={`action-btn ${selectedObject.status === 'stolen' ? 'selected' : ''}`}
                    onClick={() => updateObjectStatus(selectedObject.id, 'stolen')}
                  >
                    <span className="status-icon stolen"></span>
                    Furat
                  </button>
                </div>
              </div>
              
              <div className="action-section">
                <h3>Vizibilitate informații</h3>
                <div className="action-buttons">
                  <button 
                    className={`action-btn ${selectedObject.is_public ? 'selected' : ''}`}
                    onClick={() => updateVisibility(selectedObject.id, true)}
                  >
                    <span className="visibility-icon public"></span>
                    Public
                  </button>
                  <button 
                    className={`action-btn ${!selectedObject.is_public ? 'selected' : ''}`}
                    onClick={() => updateVisibility(selectedObject.id, false)}
                  >
                    <span className="visibility-icon private"></span>
                    Privat
                  </button>
                </div>
                <p className="action-note">
                  {selectedObject.is_public ? 
                    'Datele tale de contact sunt vizibile pentru oricine scanează codul QR.' : 
                    'Datele tale de contact sunt ascunse. Doar tu le poți vedea.'}
                </p>
              </div>
              
              <div className="action-section">
                <h3>Acțiuni</h3>
                <div className="action-buttons danger-zone">
                  <button 
                    className="action-btn danger"
                    onClick={() => handleDeleteObject(selectedObject.id)}
                  >
                    <span className="delete-icon"></span>
                    Șterge obiectul
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      
      {showSeedForm && (
        <div className="modal-overlay">
          <div className="modal-container">
            <div className="modal-header">
              <h2>Adaugă obiect existent</h2>
              <button className="modal-close" onClick={() => setShowSeedForm(false)}>×</button>
            </div>
            <div className="modal-body">
              <p>Introdu seed-ul de 12 cuvinte pentru a adăuga un obiect existent în contul tău.</p>
              <textarea
                className="seed-input"
                value={seedInput}
                onChange={(e) => setSeedInput(e.target.value)}
                placeholder="Introdu seed-ul de 12 cuvinte..."
                rows={4}
              />
            </div>
            <div className="modal-footer">
              <button 
                className="btn primary-btn"
                onClick={handleClaimObject}
                disabled={claimLoading}
              >
                {claimLoading ? 'Se adaugă...' : 'Adaugă obiect'}
              </button>
              <button 
                className="btn secondary-btn"
                onClick={() => setShowSeedForm(false)}
              >
                Anulează
              </button>
            </div>
          </div>
        </div>
      )}
      
      {expandedQR && (
        <div className="modal-overlay" onClick={() => setExpandedQR(null)}>
          <div className="modal-container qr-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Cod QR pentru printare</h2>
              <button className="modal-close" onClick={() => setExpandedQR(null)}>×</button>
            </div>
            <div className="modal-body centered">
              <QRCode
                id={`qr-code-expanded-${expandedQR}`}
                value={`${window.location.origin}/status/${expandedQR}`}
                size={300}
                level="H"
                includeMargin={true}
              />
            </div>
            <div className="modal-footer">
              <button 
                className="btn primary-btn"
                onClick={() => {
                  const canvas = document.getElementById(`qr-code-expanded-${expandedQR}`);
                  if (canvas) {
                    const pngUrl = canvas
                      .toDataURL('image/png')
                      .replace('image/png', 'image/octet-stream');
                    
                    const downloadLink = document.createElement('a');
                    downloadLink.href = pngUrl;
                    downloadLink.download = `antifurt-qr-large-${expandedQR}.png`;
                    document.body.appendChild(downloadLink);
                    downloadLink.click();
                    document.body.removeChild(downloadLink);
                  }
                }}
              >
                Descarcă
              </button>
              <button 
                className="btn secondary-btn"
                onClick={() => setExpandedQR(null)}
              >
                Închide
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;